<template>
  <div
    v-intersect="{
      handler: visibilityChanged,
      options: {
        threshold: [0, 0.5, 1.0],
      },
    }"
    class="d-flex flex-drag-box"
  >
    <!-- 30ms delay to ensure draggable content is always clickable -->
    <draggable
      v-if="showProducts"
      :model-value="filteredProducts"
      class="drag-container d-flex flex-wrap align-content-start"
      ghost-class="ghost"
      draggable=".draggable"
      delay="30"
      item-key="productKey"
      :disabled="isDragDisabled"
      :group="groupName"
      :sort="false"
      :scroll-sensitivity="150"
      :invert-swap="true"
      :force-fallback="true"
      @change="onChange"
      @start="onDragStart"
      @end="onDragStop"
    >
      <template #item="{ element, index }">
        <div
          :id="`tile-product-${element.productKey}`"
          :key="`${element._id}-${element.isHighlighted}`"
          :index="index"
          :class="{
            draggable:
              element.lockType === lockTypes.unlocked && !isDragDisabled && inReset(element),
          }"
          class="default-box"
        >
          <ProductTile
            :index="index"
            :spacebreak-id="spacebreakId"
            :product="element"
            :selected-tile-size="selectedTileSize"
            :tile-view-ref="tileViewRef"
            :open-popup-on-load-for-product-id="productIdForPopup"
          />
        </div>
      </template>
    </draggable>
    <!-- Placeholder for when the product tile is off screen -->
    <div v-else class="d-flex flex-wrap skeleton-box">
      <div v-for="element in filteredProducts" :key="`${element._id}-wrapper`">
        <div
          :id="`tile-product-${element.productKey}`"
          :key="element._id"
          class="d-flex flex-column default-box skeleton"
          :style="{
            height: `${tileSize[selectedTileSize].tile.height}px`,
            width: `${tileSize[selectedTileSize].tile.width}px`,
          }"
          :class="{
            'medium-margin': selectedTileSize === 'medium',
          }"
        >
          <img
            :id="`product-tile-popup-${element.productKey}`"
            src="@/assets/spinner.gif"
            @click="delayPopup(element._id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Notes:
// - This component uses a combination of vue draggable and vue observe visible
// These libraries will work together, but some care is needed in ensuring that
// things are removed and (more importantly) *not* removed appropriately.
// In this case, we ensure that while a drag is happening, the original tiles
// are not disposed of when they leave the screen, otherwise they'll be removed and
// the app will lose track of what was actaully being dragged
// - The placeholder is used to ensure the correct spacing remains on rows
// and columns as the user scrolls around
// - The search auto opens the popup, but needs to cope with that popup not
// yet being rendered on the DOM. It passes in the currently searched product to
// check against, when new things are rendered. Note that the search will
// find the placeholder (if the result is offscreen), so that can be used to
// position the users screen while the DOM loads.
import { usePermission } from '@/composables/use-permission';
import { useCanvasDraggableVisibility } from '@/composables/use-canvas-draggable-visibility';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useScenarioProductsStore } from '@/stores/scenario-products';
import { useSnackbarStore } from '@/stores/snackbar';
import destroy from '@/utils/destroy';
import productUtils from '@/utils/product-utils';
import assortmentProductLockTypes from '@enums/assortment-product-lock-types';
import spacebreakTypes from '@enums/spacebreak-types';
import { get, includes } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useDomUtility } from '@/composables/dom-utils';
import { useCanvasEditability } from '@/composables/use-canvas-editability';

export default {
  props: {
    filteredProducts: {
      type: Array,
      required: true,
    },

    selectedTileSize: {
      type: String,
      required: true,
    },

    groupName: {
      type: String,
      required: true,
    },

    spacebreakId: {
      type: String,
      default: null,
    },

    tileViewRef: {
      type: Object,
      required: true,
    },

    containerId: {
      type: String,
      default: null,
    },
  },

  setup() {
    const { canEditAssortmentCanvas } = usePermission();
    const { addActiveSearchBorderToProductElement } = useDomUtility();
    const {
      delayPopup,
      onDragStart,
      onDragStop,
      productIdForPopup,
      showProducts,
      visibilityChanged,
    } = useCanvasDraggableVisibility();

    const { canvasCanBeEdited } = useCanvasEditability();

    return {
      canvasCanBeEdited,
      canEditAssortmentCanvas,
      addActiveSearchBorderToProductElement,
      delayPopup,
      onDragStart,
      onDragStop,
      productIdForPopup,
      showProducts,
      visibilityChanged,
    };
  },

  data() {
    return {
      lockTypes: assortmentProductLockTypes.lockTypes,
      get,
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'referenceCheckpointProducts',
      'unifiedColumns',
      'tileSize',
      'spacebreaksOrderedObject',
      'isSpacebreakLocked',
      'getIndexedCanvasProducts',
    ]),

    isDragDisabled() {
      return (
        this.isSpacebreakLocked(this.spacebreakId) ||
        includes(spacebreakTypes.undraggableSpacebreaks, this.containerId) ||
        !this.canvasCanBeEdited
      );
    },
  },

  beforeUnmount() {
    destroy.destroyReactiveVueProps(this);
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, [
      'updateCanvasProduct',
      'updateAssortmentCanvasProductById',
    ]),
    ...mapActions(useSnackbarStore, ['showError']),
    ...mapActions(useScenarioProductsStore, ['getAssociatedPallets']),

    onChange(changedData) {
      if (changedData.added) {
        const element = changedData.added.element;

        // Update data
        productUtils.moveProductToNewSpacebreak(
          element,
          this.spacebreakId,
          {
            updateAssortmentCanvasProductById: this.updateAssortmentCanvasProductById,
            updateCanvasProduct: this.updateCanvasProduct,
            showError: this.showError,
            spacebreaksOrderedObject: this.spacebreaksOrderedObject,
            getAssociatedPallets: this.getAssociatedPallets,
            getIndexedCanvasProducts: this.getIndexedCanvasProducts,
            $t: this.$t,
          },
          this.$t('assortmentCanvas.invalidPallets')
        );

        // Flash product border to make clear what was moved
        setTimeout(() => {
          const productTileId = `tile-product-${element.productKey}`;
          const productTileElement = document.getElementById(productTileId);
          const productElement = productTileElement?.firstChild;

          if (productElement) {
            this.addActiveSearchBorderToProductElement(productElement);
          }
        }, 200);
      }
    },

    inReset(product) {
      return get(product, 'inReset', false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.flex-drag-box {
  padding: 5px;
  width: 100%;
  height: 100%;
}

.skeleton-box {
  align-content: baseline;

  .skeleton {
    img {
      width: 40px;
      height: 40px;
      margin: auto;
    }
    position: relative;
  }
}

.medium-margin {
  margin: 5px;
}

:deep(.sortable-fallback) {
  display: none !important;
}
</style>
